import * as Tooltip from '@radix-ui/react-tooltip';
import cls from 'classnames';
import Lottie from 'lottie-react';
import buttonLoading from '../../assets/lottie/button_loading.json';
import styles from './styles.module.scss';
import { ButtonProps } from './types';

const Button = ({
    asChild = false,
    size = '3',
    variant = 'solid',
    color = 'primary',
    radius = 'medium',
    loading = false,
    fullWidth = false,
    disabled = false,
    className,
    children,
    tips,
    ...props
}: ButtonProps) => {
    // 构建 CSS 类名
    const buttonClasses = cls(
        styles.button,
        styles[`size${size}`],
        styles[`radius${radius.charAt(0).toUpperCase() + radius.slice(1)}`],
        styles[variant],
        styles[color],
        {
            [styles.loading]: loading,
            [styles.fullWidth]: fullWidth,
        },
        className
    );

    // 处理禁用状态
    const isDisabled = disabled || loading;

    // 渲染按钮内容的函数
    const renderButtonContent = () => {
        // 如果使用 asChild，渲染为 span 或其他元素
        if (asChild) {
            return (
                <span
                    className={buttonClasses}
                    role="button"
                    tabIndex={isDisabled ? -1 : 0}
                    aria-disabled={isDisabled}
                    {...props}
                >
                    <span className={styles.buttonContent}>
                        {children}
                    </span>
                    {loading ? (
                        <div className={styles.loadingSpinner}>
                            <Lottie
                                animationData={buttonLoading}
                                loop={true}
                                style={{
                                    width: '100%', height: '100%', color: 'var(--color-text)' 
                                }}
                            />
                        </div>
                    ) : null}
                </span>
            );
        }

        // 默认渲染为 button 元素
        return (
            <button
                className={buttonClasses}
                disabled={isDisabled}
                {...props}
            >
                <span className={styles.buttonContent}>
                    {children}
                </span>
                {loading ? (
                    <div className={styles.loadingSpinner}>
                        <Lottie
                            animationData={buttonLoading}
                            loop={true}
                            style={{
                                width: '100%', 
                                height: '100%',
                                filter: 'var(--lottie-filter)'
                            }}
                        />
                    </div>
                ) : null}
            </button>
        );
    };

    // 如果有 tips，使用 Tooltip 包装
    if (tips) {
        return (
            <Tooltip.Provider>
                <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                        {renderButtonContent()}
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content 
                            className={styles['tooltip-text']}
                            side="top" 
                            align="center"
                            sideOffset={8}
                        >
                            {tips}
                            <Tooltip.Arrow className={styles['tooltip-arrow']} />
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root>
            </Tooltip.Provider>
        );
    }

    // 没有 tips 时直接渲染按钮
    return renderButtonContent();
};

export { Button };
export type { ButtonProps };

